import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabaseAuthService, SupabaseUser } from '../supabase/auth.service';
import { supabase } from '../config/supabase';

interface AuthContextType {
  user: SupabaseUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper pour mapper un user Supabase vers notre format
const mapSupabaseUser = (user: any): SupabaseUser => ({
  id: user.id,
  uid: user.id,
  email: user.email,
  displayName: user.user_metadata?.full_name || user.user_metadata?.name || null,
  photoURL: user.user_metadata?.avatar_url || user.user_metadata?.picture || null,
  emailVerified: !!user.email_confirmed_at,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔐 [Auth] Initialisation...');
    let isMounted = true;

    // On se fie uniquement à onAuthStateChange qui est plus fiable
    // Le premier événement sera INITIAL_SESSION avec la session existante (ou null)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      console.log('🔐 [Auth] onAuthStateChange:', event, session?.user?.email || 'pas de user');

      if (session?.user) {
        const mappedUser = mapSupabaseUser(session.user);
        // 🔧 FIX: Ne pas créer un nouvel objet si l'utilisateur n'a pas changé
        // Évite les cycles de cleanup/restart dans les contextes enfants
        setUser(prev => {
          if (prev?.id === mappedUser.id &&
              prev.emailVerified === mappedUser.emailVerified &&
              prev.displayName === mappedUser.displayName &&
              prev.photoURL === mappedUser.photoURL &&
              prev.email === mappedUser.email) {
            return prev; // Garder la même référence
          }
          return mappedUser;
        });

        // Log email verification status for debugging
        if (event === 'USER_UPDATED') {
          console.log('🔐 [Auth] USER_UPDATED - emailVerified:', mappedUser.emailVerified);
        }

        // S'assurer que le profil organisateur existe (pour OAuth, email confirmé, etc.)
        if (event === 'SIGNED_IN') {
          console.log('🔐 [Auth] SIGNED_IN - vérification/création profil organisateur...');
          // Fire and forget - ne pas bloquer le loading
          supabaseAuthService.ensureOrganizerProfile(session.user).catch(err => {
            console.error('❌ [Auth] Erreur création profil:', err);
          });
        }
      } else {
        setUser(null);
      }

      // Toujours mettre loading à false après le premier événement
      if (loading) {
        console.log('✅ [Auth] Premier événement reçu, loading → false');
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Méthodes d'authentification
  const login = async (email: string, password: string) => {
    const user = await supabaseAuthService.login(email, password);
    setUser(user);
  };

  const register = async (email: string, password: string) => {
    const user = await supabaseAuthService.register(email, password);
    setUser(user);
  };

  const signInWithGoogle = async () => {
    await supabaseAuthService.signInWithGoogle();
  };

  const logout = async () => {
    await supabaseAuthService.logout();
    setUser(null);
  };

  const resetPassword = async (email: string) => {
    await supabaseAuthService.resetPassword(email);
  };

  const resendVerificationEmail = async () => {
    if (!user?.email) {
      throw new Error('No user email available');
    }
    await supabaseAuthService.resendVerificationEmail(user.email);
  };

  const value = {
    user,
    loading,
    login,
    register,
    signInWithGoogle,
    logout,
    resetPassword,
    resendVerificationEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
